'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { CommunityInstructorIdsContext } from '@/features/course-landing-page/context/community-instructor-ids-context';
import { useInstructorCommunityBannerInfoByInstructorIdQuery } from '@/generated/graphql';

/**
 * Feature code gating the instructor community badges/ribbon/banner.
 * Monolith: ExpPlatform.get_feature_variant("instructor_community")
 */
export const FEATURE_INSTRUCTOR_COMMUNITY = 'instructor_community';

/**
 * Reads the community id off the banner info payload as a number.
 *
 * GraphQL types the id as `ID` (a string) while the event schema declares a `long`, so it is
 * converted here. Anything that is not a number yields `undefined`, and callers skip the event
 * rather than publish a bogus id.
 */
export function readInstructorCommunityId(bannerInfo: {
  id?: string | number | null;
} | null | undefined): number | undefined {
  const id = bannerInfo?.id;
  if (id == null || id === '') {
    return undefined;
  }
  const numericId = Number(id);
  return Number.isFinite(numericId) ? numericId : undefined;
}

/**
 * Fetches an instructor's published community banner info via GraphQL
 * (instructorCommunityBannerInfoByInstructorId), gated behind the
 * instructor_community experiment. Returns null/false when the experiment
 * is disabled for the current viewer, or when the instructor has no
 * published community.
 *
 * When the server resolved which of the course's instructors own a community, the query is skipped
 * for the others. If that list is unknown (undefined), the query runs as before.
 */
export function useInstructorCommunityBannerInfo(instructorId) {
  const $ = _c(18);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [FEATURE_INSTRUCTOR_COMMUNITY];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  let t1;
  if ($[1] !== featureVariantAssignments) {
    t1 = featureVariantAssignments.get(FEATURE_INSTRUCTOR_COMMUNITY)?.configuration?.enabled;
    $[1] = featureVariantAssignments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isExperimentEnabled = t1 === true;
  const communityInstructorIds = useContext(CommunityInstructorIdsContext);
  let t2;
  if ($[3] !== communityInstructorIds || $[4] !== instructorId) {
    t2 = communityInstructorIds !== undefined && !communityInstructorIds.includes(String(instructorId));
    $[3] = communityInstructorIds;
    $[4] = instructorId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isKnownToHaveNoCommunity = t2;
  const t3 = String(instructorId);
  let t4;
  if ($[6] !== t3) {
    t4 = {
      instructorId: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = instructorId != null && isExperimentEnabled && !isKnownToHaveNoCommunity;
  let t6;
  if ($[8] !== t5) {
    t6 = {
      enabled: t5
    };
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const query = useInstructorCommunityBannerInfoByInstructorIdQuery(t4, t6);
  const bannerInfo = isExperimentEnabled ? query.data?.instructorCommunityBannerInfoByInstructorId ?? null : null;
  let t7;
  if ($[10] !== bannerInfo) {
    t7 = readInstructorCommunityId(bannerInfo);
    $[10] = bannerInfo;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = isExperimentEnabled && bannerInfo != null;
  const t9 = isExperimentEnabled && query.isLoading;
  const t10 = (query.error ?? undefined) as Error | undefined;
  let t11;
  if ($[12] !== bannerInfo || $[13] !== t10 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t11 = {
      bannerInfo,
      communityId: t7,
      hasCommunity: t8,
      isLoading: t9,
      error: t10
    };
    $[12] = bannerInfo;
    $[13] = t10;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}